/**
 * materialManagerTypes — Internal types used by MaterialManager.
 *
 * Kept in a separate file to keep MaterialManager.ts under the line limit
 * while remaining importable for tests that inspect slot internals.
 *
 * @see docs/ims-engine-api/06-feature-color-system.md
 */

import * as THREE from 'three';
import type { AssetLoader } from './AssetLoader';

export type { AssetLoader };

// ---------------------------------------------------------------------------
// Internal slot & lerp types
// ---------------------------------------------------------------------------

/** Internal state per managed material slot. */
export interface ManagedSlot {
  /** The mesh(es) that use this material (name → meshes). */
  meshes: THREE.Mesh[];
  /** Currently active material (may be swapped preset). */
  current: THREE.MeshPhysicalMaterial;
  /** Original material from the GLB (kept for reset). */
  original: THREE.Material;
  /** Whether the current material is a library preset (not the original). */
  isSwapped: boolean;
  /**
   * Name of the preset currently applied (e.g. 'car-paint', 'carbon').
   * Used to detect when a different preset is requested so we re-swap
   * rather than lerp (e.g. switching from carbon back to car-paint).
   */
  activePreset: string | null;
  /**
   * The `type` value used in the last swap (e.g. 'metallic', 'glossy').
   * Combined with `activePreset` to detect when a type change within the
   * same preset requires a fresh material swap (e.g. carbon glossy → matte).
   */
  activeType: string | null;
}

/** A single active lerp task. */
export interface LerpTask {
  material: THREE.MeshPhysicalMaterial;
  startTime: number;
  duration: number;
  fromColor?: THREE.Color;
  toColor?: THREE.Color;
  fromProps?: LerpableProps;
  toProps?: LerpableProps;
  fromNormalScale?: number;
  toNormalScale?: number;
  onComplete?: () => void;
}

export interface LerpableProps {
  metalness: number;
  roughness: number;
  clearcoat: number;
  clearcoatRoughness: number;
}

/**
 * Internal state per atlas-tracked material slot.
 *
 * Unlike ManagedSlot (Preset Track), no library-preset is applied.
 * The original GLB material is cloned once so texture assignments
 * don't corrupt the shared GLTF asset cache.
 */
export interface ManagedAtlasSlot {
  /** Meshes sharing this material. */
  meshes: THREE.Mesh[];
  /** Original material from the GLB — never mutated, used for reset. */
  original: THREE.MeshStandardMaterial;
  /**
   * Working clone of `original`. All texture / color changes target this.
   * Replaced by a fresh clone on resetAtlas().
   */
  current: THREE.MeshStandardMaterial;
  /** URL of the currently applied diffuse texture (null = original from GLB). */
  activeDiffuseUrl: string | null;
  /** URL of the currently applied normal texture (null = original from GLB). */
  activeNormalUrl: string | null;
  /** URL of the currently applied metallic-roughness texture (null = original from GLB). */
  activeMetallicRoughnessUrl: string | null;
}

// ---------------------------------------------------------------------------
// Math helpers
// ---------------------------------------------------------------------------

export function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

export function easeInOutCubic(t: number): number {
  return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
}
